import classNames from "classnames";
import { useFeature } from "flagged";
import { ErrorMessage, Form, Formik, FormikConfig, FormikProps } from "formik";
import * as React from "react";
import { Menu } from "react-aria-menubutton";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import { DatePicker } from "rsuite";
import { object, string } from "yup";

import CloneDeliveryModal from "./CloneDeliveryModal";
import style from "./delivery-history.scss";
import DeleteConfirmationDialog from "components/confirmation/DeleteConfirmationDialog";
import PreviewIcon from "components/icons/PreviewIcon";
import BundleTokenPricingView from "components/licenses/BundleTokenPricingView";
import {
    createLicenseTypes,
    NON_DELIVERABLE_FEATURE_LICENSES,
    OPPORTUNITY_ID_VALIDATION_FORMAT_1,
    OPPORTUNITY_ID_VALIDATION_FORMAT_2,
} from "components/licenses/common";
import tokenstyle from "components/licenses/delivery-history/add-license-delivery.scss";
import {
    DESCRIPTION_MAX_LENGTH,
    MAX_CASE_NUMBER_LENGTH,
    OPPORTUNITY_ID_LENGTH,
} from "components/licenses/delivery-history/DeliveryFormContent";
import {
    createProductsMap,
    fetchRateVersionNameFromUuid,
} from "components/licenses/delivery-history/ViewDeliveryHistory";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import MenuItemButton from "components/menu-item-button/MenuItemButton";
import Modal from "components/modal/Modal";
import StaticTable from "components/support/api-guide/StaticTable";
import DateCell from "components/table/DateCell";
import { deduceTierLicenses } from "components/tenants/add-tenant/AddTenantForm";
import tenantStyle from "components/tenants/tenants.scss";
import Tooltip from "components/tooltip/Tooltip";
import { DeliveryStatus, LicenseDelivery } from "domain/licenses";
import { FLAG_SCHEDULED_LICENSE_DELIVERY } from "services/feature/FeatureFlagService";
import { extractProductRatesList, licenseService, ProductToRateList } from "services/licenses/LicenseService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import {
    getTenantName,
    hasSubTenantCookie,
    isLicensingModelBundleWithToken,
    isUserParentInternal,
} from "services/tenants/tenantCookieService";
import { StoreState } from "store";
import { replaceProductIdWithNameInRatesTable } from "store/license";
import { switchTheme } from "store/theme";
import buttons from "styles/buttons.scss";
import form from "styles/form.scss";
import { hasOnlyNumbers } from "utils/commonFunctions";
import { formatDateWithoutTime } from "utils/format";
import { useTheme } from "utils/useTheme";

import alert from "assets/images/icons/alert.svg";

import testIds from "testIds.json";

const mapState = (state: StoreState) => ({
    themeName: state.themeReducer.themeName,
    theme: state.themeReducer.theme,
    tenantDetails: state.tenantDetailsReducer.stack[state.tenantDetailsReducer.stack.length - 1],
});

export interface Result {
    title: string;
    message: string;
    succeeded?: boolean;
}

export interface DeliveryLicenses {
    licenseId: string;
    amount: number;
    expirationDate: string;
}

export interface UserInfo {
    addedBy: string;
    addedDate: string;
}

export interface EditDeliveryForm {
    caseNumber: string;
    opportunityId: string;
    notes: string;
    licenseRateVersion?: string;
    status?: string;
    targetDate?: string;
}

interface CloneDeliveryData {
    deliveryType: string;
    caseNumber: string;
    opportunityId: string;
    notes: string;
}

const connector = connect(mapState, { switchTheme });
const ALL_PRODUCTS = createProductsMap();

const isDeliveryFormDisabled = (status?: string): boolean => {
    return (
        status === DeliveryStatus.DELIVERED ||
        status === DeliveryStatus.DELETED ||
        status === DeliveryStatus.TRIGGERED_MANUALLY ||
        !status
    );
};

const DeliveryHistoryMenuItems = (
    props: {
        deliveryHistoryId: string;
        onDeliveryHistoryEdit: (isSync: boolean) => void;
        tokenRateVersions: ProductToRateList[];
        rateUuid: string;
        deliveryStatus?: string;
    } & ConnectedProps<typeof connector>
): JSX.Element => {
    const { t } = useTranslation();

    const [initialLoading, setInitialLoading] = React.useState<boolean>(true);
    const [editDeliveryHistoryVisible, setEditDeliveryHistoryVisible] = React.useState(false);
    const { current: abortControllers } = React.useRef<AbortController[]>([]);
    const [result, setResult] = React.useState<Result>({ title: "", message: "" });
    const [resultVisible, setResultVisible] = React.useState(false);
    const [formInitialState, setFormInitialState] = React.useState<EditDeliveryForm>();
    const [initiateLicenseDeliveryState, setInitiateLicenseDeliveryState] = React.useState<LicenseDelivery>();
    const [userInformation, setUserInformation] = React.useState<UserInfo>();
    const [licenses, setLicenses] = React.useState<DeliveryLicenses[]>();
    const [loadingOnEdit, setLoadingOnEdit] = React.useState(false);
    const [deleteDialogVisible, setDeleteDialogVisible] = React.useState(false);
    const [handleCloneDialog, setHandleCloneDialog] = React.useState(false);
    const [deletingDelivery, setDeletingDelivery] = React.useState(false);
    const modalTitle = t("DeliveryHistory.editLicenseDelivery.title", { customer: getTenantName() });
    const [triggeringDelivery, setTriggeringDelivery] = React.useState(false);
    const [triggerDeliveryValue, setTriggerDeliveryValue] = React.useState(false);
    const [cloneDeliveryData, setCloneDeliveryData] = React.useState<CloneDeliveryData>();
    const [menuCloseTrigger, setMenuCloseTrigger] = React.useState(false);

    const showResult = (resultToShow: Result) => {
        setResultVisible(true);
        setResult(resultToShow);
    };

    const handleDelete = () => {
        setDeleteDialogVisible(true);
    };
    const handleDeleteConfirm = async () => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        setDeletingDelivery(true);

        try {
            await licenseService.editLicenseDelivery(
                props.deliveryHistoryId,
                {
                    notes: formInitialState?.notes || "",
                    caseNumber: formInitialState?.caseNumber || "",
                    opportunityId: formInitialState?.opportunityId || "",
                    status: DeliveryStatus.DELETED,
                },
                abortController,
                props.tenantDetails.region
            );

            showResult({
                title: t("DeliveryHistory.deleteDelivery.successTitle"),
                message: t("DeliveryHistory.deleteDelivery.successMessage"),
            });
            setDeleteDialogVisible(false);
        } catch {
            showResult({
                title: t("DeliveryHistory.deleteDelivery.failureTitle"),
                message: t("DeliveryHistory.deleteDelivery.failureMessage"),
            });
        } finally {
            setDeletingDelivery(false);
        }
    };
    const [bundleTokenPricingModalVisible, setBundleTokenPricingModalVisible] = React.useState(false);
    const theme = useTheme();
    const deducedTierLicenses = deduceTierLicenses(props.tenantDetails.tenantTier, true, true);

    const submitHandler: FormikConfig<EditDeliveryForm>["onSubmit"] = async ({
        caseNumber,
        notes,
        opportunityId,
        status,
        targetDate,
    }: EditDeliveryForm): Promise<void> => {
        const abortController = new AbortController();
        abortControllers.push(abortController);
        setLoadingOnEdit(true);

        const payload: {
            notes: string;
            caseNumber: string;
            opportunityId: string;
            status?: string;
            targetDate?: string;
        } = {
            notes: notes,
            caseNumber: caseNumber,
            opportunityId: opportunityId,
        };

        if (status === DeliveryStatus.SCHEDULED || status === DeliveryStatus.ON_HOLD) {
            payload.status = status;
            payload.targetDate = targetDate;
        }

        await licenseService
            .editLicenseDelivery(props.deliveryHistoryId, payload, abortController, props.tenantDetails.region)
            .then(() => {
                showResult({
                    title: t("DeliveryHistory.editLicenseDelivery.successTitle"),
                    message: t("DeliveryHistory.editLicenseDelivery.successMessage"),
                });
                setEditDeliveryHistoryVisible(false);
                setLoadingOnEdit(false);
            })
            .catch(() => {
                setEditDeliveryHistoryVisible(false);
                setLoadingOnEdit(false);
                showResult({
                    title: t("DeliveryHistory.editLicenseDelivery.failureTitle"),
                    message: t("DeliveryHistory.editLicenseDelivery.failureMessage"),
                });
                setEditDeliveryHistoryVisible(true);
            })
            .finally(() => {
                setLoadingOnEdit(false);
                setResultVisible(true);
            });
    };

    const triggerLicenseDeliveryNow = async (): Promise<void> => {
        if (!initiateLicenseDeliveryState) {
            return;
        }
        setTriggeringDelivery(true);
        setMenuCloseTrigger(false);
        const abortController = new AbortController();
        abortControllers.push(abortController);

        try {
            await licenseService.createLicenseDelivery(
                {
                    ...initiateLicenseDeliveryState,
                },
                abortController,
                props.tenantDetails.region
            );

            showResult({
                title: t("DeliveryHistory.addLicenseDelivery.successTitle"),
                message: t("DeliveryHistory.addLicenseDelivery.successMessage", {
                    customerName: props.tenantDetails.uuid == null ? getTenantName() : props.tenantDetails.tenantName,
                }),
                succeeded: true,
            });
        } catch {
            showResult({
                title: t("DeliveryHistory.addLicenseDelivery.failureTitle"),
                message: t("DeliveryHistory.addLicenseDelivery.failureMessage"),
                succeeded: false,
            });
        } finally {
            setTriggeringDelivery(false);
            setResultVisible(true);
        }
    };

    React.useEffect(() => {
        if (initiateLicenseDeliveryState && triggerDeliveryValue) {
            triggerLicenseDeliveryNow();
            setTriggerDeliveryValue(false);
        }
    }, [triggerDeliveryValue]);

    const fetchDeliveryHistoryDetails = (uuid: string, shouldTrigger: boolean = false) => {
        setInitialLoading(true);
        setMenuCloseTrigger(false);
        const abortController = new AbortController();
        abortControllers.push(abortController);
        const fetchLicenseDeliveries = licenseService.fetchLicenseDeliveryHistory(uuid, abortController);
        Promise.all([fetchLicenseDeliveries, props.tokenRateVersions])
            .then(([fetchedLicenseDeliveries, fetchedRateVersions]) => {
                const deliveryVersionName = fetchRateVersionNameFromUuid(
                    fetchedLicenseDeliveries.licenseRateVersion,
                    fetchedRateVersions
                );
                const deliveryData: EditDeliveryForm = {
                    caseNumber: fetchedLicenseDeliveries.caseNumber,
                    opportunityId: fetchedLicenseDeliveries.opportunityId,
                    notes: fetchedLicenseDeliveries.notes,
                    licenseRateVersion: deliveryVersionName,
                    status: fetchedLicenseDeliveries.status,
                    targetDate: fetchedLicenseDeliveries.targetDate,
                };
                const cloneDeliveryData = {
                    deliveryType: fetchedLicenseDeliveries.type,
                    caseNumber: fetchedLicenseDeliveries.caseNumber,
                    opportunityId: fetchedLicenseDeliveries.opportunityId,
                    notes: fetchedLicenseDeliveries.notes,
                };
                setCloneDeliveryData(cloneDeliveryData);
                setFormInitialState(deliveryData);
                setInitialLoading(fetchedLicenseDeliveries.type == "");
                setLicenses(fetchedLicenseDeliveries.licenses);
                setUserInformation({
                    addedBy: fetchedLicenseDeliveries.creatorUuid,
                    addedDate: fetchedLicenseDeliveries.creationDate,
                });
                if (
                    shouldTrigger &&
                    props.deliveryStatus &&
                    (props.deliveryStatus === DeliveryStatus.SCHEDULED ||
                        props.deliveryStatus === DeliveryStatus.ON_HOLD)
                ) {
                    setTriggerDeliveryValue(true);
                    const initiateDelivery: LicenseDelivery = {
                        licenseDeliveryUuid: props.deliveryHistoryId,
                        caseNumber: fetchedLicenseDeliveries.caseNumber,
                        opportunityId: fetchedLicenseDeliveries.opportunityId,
                        notes: fetchedLicenseDeliveries.notes,
                        editorUuid: fetchedLicenseDeliveries.editorUuid,
                        licenses: fetchedLicenseDeliveries.licenses,
                        tenantUuid: props.tenantDetails.uuid,
                        toPopulatePools: false,
                        tokenRateVersion: fetchedLicenseDeliveries.licenseRateVersion,
                        type: fetchedLicenseDeliveries.type,
                        status: DeliveryStatus.TRIGGERED_MANUALLY,
                        creationDate: fetchedLicenseDeliveries.creationDate,
                    };
                    setInitiateLicenseDeliveryState(initiateDelivery);
                } else {
                    setTriggerDeliveryValue(false);
                }
            })
            .catch(() => {
                setInitialLoading(false);
                showResult({
                    title: modalTitle,
                    message: t("DeliveryHistory.deliveryHistoryDialog.failedToLoadLicenseDetails"),
                });
            });
    };

    const hideResultDialog = () => {
        setResultVisible(false);
        props.onDeliveryHistoryEdit(false);
    };

    React.useEffect(() => {
        return () => abortControllers.filter((a) => !a.signal.aborted).forEach((a) => a.abort());
    }, []);

    const LICENSE_TYPES = createLicenseTypes(true);
    const deliverableLicenseTypes = LICENSE_TYPES.filter(
        (element) => !NON_DELIVERABLE_FEATURE_LICENSES.includes(element.productId)
    );

    const listAddedLicenses = (): JSX.Element => {
        return result.succeeded ? (
            <div className={form.resultContainer}>
                <ul>
                    {initiateLicenseDeliveryState?.licenses.map((each) => {
                        const product = deliverableLicenseTypes.find((license) => license.productId === each.licenseId);
                        return (
                            <li key={each.licenseId}>
                                {t("DeliveryHistory.addLicenseDelivery.license", {
                                    amount: each.totalOfLicenses,
                                    productName: product?.productName,
                                    expirationDate: formatDateWithoutTime(each.expirationDate),
                                })}
                            </li>
                        );
                    })}
                </ul>
            </div>
        ) : (
            <></>
        );
    };

    const licenseStaticTable = (licenses: DeliveryLicenses[] | undefined) => {
        return (
            licenses !== undefined && (
                <StaticTable
                    headers={[
                        {
                            value: t("DeliveryHistory.editLicenseDelivery.table.licenseType"),
                        },
                        {
                            value: t("DeliveryHistory.editLicenseDelivery.table.amount"),
                        },
                        {
                            value: t("DeliveryHistory.editLicenseDelivery.table.expirationDate"),
                        },
                    ]}
                    cells={licenses.map((license, key) => {
                        return [
                            <div
                                data-testid={
                                    testIds.workArea.license.deliveryHistory.editDeliveryDialog.table.licenseTypeLabel
                                }
                                key={license.licenseId}
                            >
                                {ALL_PRODUCTS.get(license.licenseId)}
                            </div>,
                            <div
                                data-testid={
                                    testIds.workArea.license.deliveryHistory.editDeliveryDialog.table
                                        .totalOfLicensesLabel
                                }
                                key={"amount" + key}
                            >
                                {license.amount}
                            </div>,
                            <div
                                data-testid={
                                    testIds.workArea.license.deliveryHistory.editDeliveryDialog.table
                                        .expirationDateLabel
                                }
                                key={"date" + key}
                            >
                                <DateCell tooltip={false} value={license.expirationDate} />
                            </div>,
                        ];
                    })}
                    testId={testIds.workArea.license.deliveryHistory.editDeliveryDialog.table.itself}
                />
            )
        );
    };

    return (
        <div className={style.deliveryHistoryEditIcon}>
            <Menu className={tenantStyle.kebabMenu}>
                <ul>
                    <li className={tenantStyle.listItem}>
                        <MenuItemButton
                            onClick={() => {
                                fetchDeliveryHistoryDetails(props.deliveryHistoryId, false);
                                setEditDeliveryHistoryVisible(true);
                                usageStatisticsService.sendEvent({
                                    category: Category.LICENSE,
                                    action: Action.EDIT_LICENSE_DELIVERY,
                                });
                            }}
                            disabled={props.deliveryStatus === DeliveryStatus.DELETED}
                            className={tenantStyle.menuItem}
                            data-testid={testIds.workArea.license.deliveryHistory.table.kebabMenu.editButton}
                        >
                            {t("Common.edit")}
                        </MenuItemButton>
                    </li>

                    {useFeature(FLAG_SCHEDULED_LICENSE_DELIVERY) &&
                        (props.deliveryStatus == DeliveryStatus.SCHEDULED ||
                            props.deliveryStatus == DeliveryStatus.ON_HOLD) && (
                            <li className={tenantStyle.listItem}>
                                <MenuItemButton
                                    onClick={() => {
                                        setMenuCloseTrigger(true);
                                        fetchDeliveryHistoryDetails(props.deliveryHistoryId, true);
                                        usageStatisticsService.sendEvent({
                                            category: Category.LICENSE,
                                            action: Action.MANUALLY_TRIGGERED_LICENSE_DELIVERY,
                                        });
                                    }}
                                    disabled={!isUserParentInternal() || triggeringDelivery || menuCloseTrigger}
                                    className={tenantStyle.menuItem}
                                >
                                    {t("DeliveryHistory.deliveryHistoryTable.kebabMenu.initiateScheduleDelivery")}
                                </MenuItemButton>
                            </li>
                        )}
                    {useFeature(FLAG_SCHEDULED_LICENSE_DELIVERY) && (
                        <li className={tenantStyle.listItem}>
                            <MenuItemButton
                                onClick={() => {
                                    fetchDeliveryHistoryDetails(props.deliveryHistoryId, false);
                                    setHandleCloneDialog(true);
                                }}
                                className={tenantStyle.menuItem}
                            >
                                {t("DeliveryHistory.deliveryHistoryTable.kebabMenu.cloneDelivery")}
                            </MenuItemButton>
                        </li>
                    )}

                    {props.deliveryStatus !== DeliveryStatus.TRIGGERED_MANUALLY &&
                        props.deliveryStatus !== DeliveryStatus.DELIVERED &&
                        !!props.deliveryStatus && (
                            <li className={tenantStyle.listItem}>
                                <MenuItemButton
                                    onClick={handleDelete}
                                    disabled={!isUserParentInternal()}
                                    className={tenantStyle.menuItem}
                                >
                                    {t("Common.delete")}
                                </MenuItemButton>
                            </li>
                        )}
                </ul>
            </Menu>
            <DeleteConfirmationDialog
                modalDisplayed={deleteDialogVisible}
                setModalDisplayed={setDeleteDialogVisible}
                title={t("DeliveryHistory.deleteDelivery.title")}
                introductionMessage={t("DeliveryHistory.deleteDelivery.introductionMessage")}
                confirmationMessage={t("DeliveryHistory.deleteDelivery.confirmationMessage")}
                handleCommand={handleDeleteConfirm}
                loading={deletingDelivery}
            />
            <CloneDeliveryModal
                isOpen={handleCloneDialog}
                onClose={() => setHandleCloneDialog(false)}
                deliveryHistoryId={props.deliveryHistoryId}
                initialLoading={initialLoading}
                cloneDeliveryData={cloneDeliveryData}
                licenses={licenses}
                abortControllers={abortControllers}
                setInitialLoading={setInitialLoading}
                showResult={showResult}
            />

            <Modal
                isOpen={editDeliveryHistoryVisible}
                hideModal={() => setEditDeliveryHistoryVisible(false)}
                modalTitle={modalTitle}
            >
                <div>
                    {formInitialState !== undefined && !initialLoading && !loadingOnEdit ? (
                        <Formik
                            initialValues={{
                                caseNumber: isUserParentInternal() ? formInitialState.caseNumber : "",
                                opportunityId: isUserParentInternal() ? formInitialState.opportunityId : "",
                                notes: formInitialState.notes,
                                licenseRateVersion: formInitialState.licenseRateVersion,
                                status: formInitialState.status || DeliveryStatus.DELIVERED,
                                targetDate: formInitialState.targetDate,
                            }}
                            onSubmit={submitHandler}
                            validationSchema={object().shape({
                                caseNumber: isUserParentInternal()
                                    ? string()
                                          .required(t("DeliveryHistory.addLicenseDelivery.validation.caseNumber"))
                                          .typeError(t("DeliveryHistory.addLicenseDelivery.validation.caseNumberType"))
                                          .max(
                                              MAX_CASE_NUMBER_LENGTH,
                                              t("DeliveryHistory.addLicenseDelivery.validation.caseNumberLength", {
                                                  length: MAX_CASE_NUMBER_LENGTH,
                                              })
                                          )
                                          .test(
                                              "Has only numbers",
                                              t("DeliveryHistory.addLicenseDelivery.validation.caseNumberType"),
                                              (value: string | undefined) => hasOnlyNumbers(value)
                                          )
                                    : string()
                                          .typeError(t("DeliveryHistory.addLicenseDelivery.validation.caseNumberType"))
                                          .max(
                                              MAX_CASE_NUMBER_LENGTH,
                                              t("DeliveryHistory.addLicenseDelivery.validation.caseNumberLength", {
                                                  length: MAX_CASE_NUMBER_LENGTH,
                                              })
                                          )
                                          .test(
                                              "Has only numbers",
                                              t("DeliveryHistory.addLicenseDelivery.validation.caseNumberType"),
                                              (value: string | undefined) =>
                                                  value === undefined || hasOnlyNumbers(value)
                                          ),
                                opportunityId: isUserParentInternal()
                                    ? string()
                                          .required(t("DeliveryHistory.addLicenseDelivery.validation.opportunityId"))
                                          .matches(
                                              /^(006|test)/i,
                                              t("DeliveryHistory.addLicenseDelivery.validation.opportunityIdFormat", {
                                                  option1: OPPORTUNITY_ID_VALIDATION_FORMAT_1,
                                                  option2: OPPORTUNITY_ID_VALIDATION_FORMAT_2,
                                              })
                                          )
                                          .length(
                                              OPPORTUNITY_ID_LENGTH,
                                              t("DeliveryHistory.addLicenseDelivery.validation.opportunityIdLength", {
                                                  length: OPPORTUNITY_ID_LENGTH,
                                              })
                                          )
                                    : string()
                                          .test(
                                              "empty-length",
                                              t("DeliveryHistory.addLicenseDelivery.validation.opportunityIdLength", {
                                                  length: OPPORTUNITY_ID_LENGTH,
                                              }),
                                              (value) => {
                                                  if (!value) {
                                                      return true;
                                                  }
                                                  return value.length === OPPORTUNITY_ID_LENGTH;
                                              }
                                          )
                                          .test(
                                              "empty-format",
                                              t("DeliveryHistory.addLicenseDelivery.validation.opportunityIdFormat", {
                                                  option1: OPPORTUNITY_ID_VALIDATION_FORMAT_1,
                                                  option2: OPPORTUNITY_ID_VALIDATION_FORMAT_2,
                                              }),
                                              (value) => {
                                                  if (!value) {
                                                      return true;
                                                  }
                                                  return /^(006|test)/i.test(value);
                                              }
                                          ),
                                targetDate: string()
                                    .nullable()
                                    .test(
                                        "is-future-date",
                                        t("DeliveryHistory.editLicenseDelivery.validation.targetDate"),
                                        (value) => {
                                            if (!value) {
                                                return true;
                                            }
                                            const selectedDate = new Date(value);
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);
                                            selectedDate.setHours(0, 0, 0, 0);
                                            return selectedDate > today;
                                        }
                                    ),
                            })}
                            validateOnChange={true}
                        >
                            {({ values, errors, handleChange }: FormikProps<EditDeliveryForm>) => {
                                return (
                                    <Form>
                                        {isUserParentInternal() ? (
                                            <>
                                                <div className={form.formFields}>
                                                    <div className={form.formFieldsColumnDirection}>
                                                        {hasSubTenantCookie() ? (
                                                            <span className={form.optional}>
                                                                {t("Common.optional")}
                                                            </span>
                                                        ) : null}
                                                        <label
                                                            htmlFor="opportunityId"
                                                            className={classNames(form.label, style.wideWidth, {
                                                                [form.inputError]: errors.opportunityId,
                                                            })}
                                                        >
                                                            {t("DeliveryHistory.editLicenseDelivery.opportunityId")}
                                                        </label>
                                                    </div>

                                                    <div className={form.formFieldsColumnDirection}>
                                                        <input
                                                            id="opportunityId"
                                                            className={classNames(form.input, form.fixedWidthInput, {
                                                                [form.inputError]: errors.opportunityId,
                                                            })}
                                                            onChange={handleChange}
                                                            value={values.opportunityId}
                                                            data-testid={
                                                                testIds.workArea.license.deliveryHistory
                                                                    .editDeliveryDialog.opportunityIdInput.itself
                                                            }
                                                        />

                                                        <div
                                                            className={form.error}
                                                            data-testid={
                                                                testIds.workArea.license.deliveryHistory
                                                                    .editDeliveryDialog.opportunityIdInput.errorLabel
                                                            }
                                                        >
                                                            <ErrorMessage name="opportunityId" />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className={form.formFields}>
                                                    <div className={form.formFieldsColumnDirection}>
                                                        {hasSubTenantCookie() ? (
                                                            <span className={form.optional}>
                                                                {t("Common.optional")}
                                                            </span>
                                                        ) : null}
                                                        <label
                                                            htmlFor="caseNumber"
                                                            className={classNames(form.label, style.wideWidth, {
                                                                [form.inputError]: errors.caseNumber,
                                                            })}
                                                        >
                                                            {t("DeliveryHistory.editLicenseDelivery.caseNumber")}
                                                        </label>
                                                    </div>

                                                    <div className={form.formFieldsColumnDirection}>
                                                        <input
                                                            id="caseNumber"
                                                            className={classNames(form.input, form.fixedWidthInput, {
                                                                [form.inputError]: errors.caseNumber,
                                                            })}
                                                            onChange={handleChange}
                                                            value={values.caseNumber}
                                                            data-testid={
                                                                testIds.workArea.license.deliveryHistory
                                                                    .editDeliveryDialog.caseNumberInput.itself
                                                            }
                                                        />
                                                        <div
                                                            className={form.error}
                                                            data-testid={
                                                                testIds.workArea.license.deliveryHistory
                                                                    .editDeliveryDialog.caseNumberInput.errorLabel
                                                            }
                                                        >
                                                            <ErrorMessage name="caseNumber" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        ) : null}

                                        {
                                            <>
                                                <div className={form.formFields}>
                                                    <div className={form.formFieldsColumnDirection}>
                                                        <span className={form.optional}>{t("Common.optional")}</span>
                                                        <label
                                                            htmlFor="targetDate"
                                                            className={classNames(form.label, style.wideWidth, {
                                                                [form.inputError]: errors.targetDate,
                                                            })}
                                                        >
                                                            {t("DeliveryHistory.editLicenseDelivery.targetDate")}
                                                        </label>
                                                    </div>

                                                    <div className={form.formFieldsColumnDirection}>
                                                        <DatePicker
                                                            id="targetDate"
                                                            className={classNames(
                                                                form.dateRange,
                                                                style.fixedWidthDatePicker,
                                                                {
                                                                    [form.inputError]: errors.targetDate,
                                                                }
                                                            )}
                                                            value={
                                                                values.targetDate ? new Date(values.targetDate) : null
                                                            }
                                                            onChange={(date: Date | null) => {
                                                                handleChange({
                                                                    target: {
                                                                        name: "targetDate",
                                                                        value: date ? date.toISOString() : "",
                                                                    },
                                                                });
                                                            }}
                                                            format="dd/MM/yyyy"
                                                            cleanable={false}
                                                            placeholder={t("Common.selectRange")}
                                                            shouldDisableDate={(date: Date) => {
                                                                const today = new Date();
                                                                today.setHours(0, 0, 0, 0);
                                                                return date <= today;
                                                            }}
                                                            disabled={isDeliveryFormDisabled(formInitialState.status)}
                                                            data-testid={
                                                                testIds.workArea.license.deliveryHistory
                                                                    .editDeliveryDialog.targetDateInput.itself
                                                            }
                                                        />
                                                        <div
                                                            className={form.error}
                                                            data-testid={
                                                                testIds.workArea.license.deliveryHistory
                                                                    .editDeliveryDialog.targetDateInput.errorLabel
                                                            }
                                                        >
                                                            <ErrorMessage name="targetDate" />
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className={form.formFields}>
                                                    <label
                                                        htmlFor="status"
                                                        className={classNames(form.label, style.wideWidth)}
                                                    >
                                                        {t("DeliveryHistory.editLicenseDelivery.status")}
                                                    </label>
                                                    <select
                                                        id="status"
                                                        className={classNames(form.select, form.fixedWidthInput)}
                                                        onChange={handleChange}
                                                        value={values.status}
                                                        disabled={isDeliveryFormDisabled(formInitialState.status)}
                                                        data-testid={
                                                            testIds.workArea.license.deliveryHistory.editDeliveryDialog
                                                                .statusSelect.itself
                                                        }
                                                    >
                                                        {(formInitialState.status === DeliveryStatus.SCHEDULED ||
                                                            formInitialState.status === DeliveryStatus.ON_HOLD) && (
                                                            <>
                                                                <option value={DeliveryStatus.SCHEDULED}>
                                                                    {t(
                                                                        "DeliveryHistory.deliveryHistoryTable.statusFilter.scheduled"
                                                                    )}
                                                                </option>
                                                                <option value={DeliveryStatus.ON_HOLD}>
                                                                    {t(
                                                                        "DeliveryHistory.deliveryHistoryTable.statusFilter.onHold"
                                                                    )}
                                                                </option>
                                                            </>
                                                        )}
                                                        {(formInitialState.status === DeliveryStatus.DELIVERED ||
                                                            !formInitialState.status) && (
                                                            <option value={DeliveryStatus.DELIVERED}>
                                                                {t(
                                                                    "DeliveryHistory.deliveryHistoryTable.statusFilter.delivered"
                                                                )}
                                                            </option>
                                                        )}
                                                        {formInitialState.status ===
                                                            DeliveryStatus.TRIGGERED_MANUALLY && (
                                                            <option value={DeliveryStatus.TRIGGERED_MANUALLY}>
                                                                {t(
                                                                    "DeliveryHistory.deliveryHistoryTable.statusFilter.triggered_manually"
                                                                )}
                                                            </option>
                                                        )}
                                                    </select>
                                                </div>
                                            </>
                                        }

                                        <div className={form.formFields}>
                                            <div className={form.formFieldsColumnDirection}>
                                                <span className={form.optional}>{t("Common.optional")}</span>
                                                <label
                                                    htmlFor="notes"
                                                    className={classNames(form.label, style.wideWidth)}
                                                >
                                                    {t("DeliveryHistory.editLicenseDelivery.notes")}
                                                </label>
                                            </div>
                                            <div className={classNames(style.gridRows, form.optional)}>
                                                <textarea
                                                    id="notes"
                                                    className={classNames(
                                                        form.input,
                                                        form.fixedWidthInput,
                                                        form.textAreaHeight
                                                    )}
                                                    onChange={handleChange}
                                                    data-testid={
                                                        testIds.workArea.license.deliveryHistory.editDeliveryDialog
                                                            .notesTextArea.itself
                                                    }
                                                    maxLength={DESCRIPTION_MAX_LENGTH}
                                                    value={values.notes}
                                                />
                                                <span className={form.notes}>
                                                    {t("DeliveryHistory.addLicenseDelivery.validation.notes", {
                                                        remainingCharacters: (
                                                            DESCRIPTION_MAX_LENGTH -
                                                            (values.notes ? values.notes.length : 0)
                                                        ).toString(),
                                                        maximumNumberOfCharacters: DESCRIPTION_MAX_LENGTH.toString(),
                                                    })}
                                                </span>
                                            </div>
                                            <Tooltip content={t("DeliveryHistory.addLicenseDelivery.warning")}>
                                                <div className={style.warningIcon} tabIndex={0}>
                                                    <img src={alert} alt={t("AltText.alertIcon")} />
                                                </div>
                                            </Tooltip>
                                        </div>
                                        {isUserParentInternal() && isLicensingModelBundleWithToken() && (
                                            <div className={form.formFields}>
                                                <div className={form.formFieldsColumnDirection}>
                                                    <label
                                                        htmlFor="licenseRateVersion"
                                                        className={classNames(form.label, style.wideWidth)}
                                                    >
                                                        {t("DeliveryHistory.editLicenseDelivery.licenseRateVersion")}
                                                    </label>
                                                </div>
                                                <div className={classNames(style.gridRows, form.optional)}>
                                                    <div className={classNames(style.gridRows, form.optional)}>
                                                        {values.licenseRateVersion}
                                                    </div>
                                                </div>
                                                <div>
                                                    <button
                                                        className={classNames(
                                                            tokenstyle.tooltipPosition,
                                                            buttons.textButton
                                                        )}
                                                        type="button"
                                                        onClick={() => {
                                                            setBundleTokenPricingModalVisible(true);
                                                        }}
                                                    >
                                                        <div>
                                                            <PreviewIcon color={theme.iconFillColor} />
                                                            <span className={tokenstyle.addMoreButton}>
                                                                {t(
                                                                    "DeliveryHistory.addLicenseDelivery.previewRateTable"
                                                                )}
                                                            </span>
                                                        </div>
                                                    </button>
                                                </div>
                                                <BundleTokenPricingView
                                                    visibility={bundleTokenPricingModalVisible}
                                                    setVisibility={setBundleTokenPricingModalVisible}
                                                    productToRate={replaceProductIdWithNameInRatesTable(
                                                        extractProductRatesList(
                                                            props.tokenRateVersions,
                                                            props.rateUuid
                                                        ),
                                                        deducedTierLicenses
                                                    )}
                                                />
                                            </div>
                                        )}
                                        {userInformation && (
                                            <>
                                                <div className={form.formFields}>
                                                    <div className={form.formFieldsColumnDirection}>
                                                        <label
                                                            htmlFor="addedBy"
                                                            className={classNames(form.label, style.wideWidth)}
                                                        >
                                                            {t("DeliveryHistory.editLicenseDelivery.creatorUuid")}
                                                        </label>
                                                    </div>
                                                    <div className={classNames(style.gridRows, form.optional)}>
                                                        {userInformation.addedBy}
                                                    </div>
                                                </div>
                                                <div className={form.formFields}>
                                                    <div className={form.formFieldsColumnDirection}>
                                                        <label
                                                            htmlFor="addedDate"
                                                            className={classNames(form.label, style.wideWidth)}
                                                        >
                                                            {t("DeliveryHistory.editLicenseDelivery.creationDate")}
                                                        </label>
                                                    </div>
                                                    <div className={classNames(style.gridRows, form.optional)}>
                                                        <DateCell
                                                            tooltip={false}
                                                            value={userInformation.addedDate}
                                                            withoutTime={true}
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        {licenseStaticTable(licenses)}

                                        <div className={form.buttonContainer}>
                                            <button
                                                type="submit"
                                                className={classNames(
                                                    buttons.primaryButtonWithoutIcon,
                                                    form.submitButton
                                                )}
                                            >
                                                {t("Common.save")}
                                            </button>
                                        </div>
                                    </Form>
                                );
                            }}
                        </Formik>
                    ) : (
                        <div>
                            <LoadingIndicator />
                        </div>
                    )}
                </div>
            </Modal>

            <Modal isOpen={resultVisible} hideModal={hideResultDialog} modalTitle={result.title}>
                <div className={style.resultContainer}>{result.message}</div>
                {result.succeeded && initiateLicenseDeliveryState && listAddedLicenses()}
                <div className={form.okButtonContainer}>
                    <button className={buttons.primaryOkButton} onClick={hideResultDialog}>
                        {t("Common.ok")}
                    </button>
                </div>
            </Modal>
            <Modal
                isOpen={triggeringDelivery}
                hideModal={() => {}}
                modalTitle={t("DeliveryHistory.addLicenseDelivery.title", { customer: getTenantName() })}
            >
                <div>
                    <LoadingIndicator />
                </div>
            </Modal>
        </div>
    );
};

export default connector(DeliveryHistoryMenuItems);
